import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link, useParams, useSearchParams } from 'react-router-dom';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardHeader, Input, Modal, Select, BackButton } from '@/components/common';
import { ProviderLocator } from '@/components/directory/ProviderLocator';
import type { LocatorProvider } from '@/services/hospitalNetwork';
import { SERVER_CHECKLIST_CLAIM_TYPES, claimChecklistApi } from './claimDocuments';
import { PolicyNumber } from '@/components/common/PolicyNumber';
import { useCreateClaim, useClaimTypes, useClaim, useUpdateClaim } from '@/hooks/useClaims';
import { usePolicy } from '@/hooks';
import { useAuthStore } from '@/store';
import type { ClaimCreateRequest } from '@/types';
import { FIR_STATUS_LABELS, packClaimExtras, mergeClaimExtras } from './claimFormExtras';
import { insurerIdOf } from './insurerRef';

const claimSchema = z.object({
  policy: z.string().min(1, 'Policy is required'),
  claim_type: z.string().min(1, 'Claim type is required'),
  incident_date: z.string().min(1, 'Incident date is required'),
  incident_time: z.string().min(1, 'Incident time is required'),
  incident_location: z.string().optional(),
  incident_description: z.string().min(10, 'Description must be at least 10 characters'),
  // B7: THE AMOUNT BEING CLAIMED, ASKED OF EVERY ACTOR.
  //
  // This form sent no amount for ANYBODY -- there was no field here at all,
  // even though the broker API had accepted `claimed_amount` the whole time and
  // `ClaimCreateRequest` in types/index.ts declared it REQUIRED. The type and
  // the form had simply disagreed, silently, because `onSubmit` casts through
  // `unknown` on its way to the mutation.
  //
  // Asked of the customer too, which REVERSES an earlier decision that the
  // claimant does not name a figure at intake (the broker was to set it later).
  // See the note on `CustomerClaimCreateSerializer.Meta.fields`.
  //
  // REQUIRED HERE, OPTIONAL ON THE API. The column is nullable on purpose
  // (migration 0004) and a claim intimated with no figure yet must still be
  // recordable through the API; but a person filling in this form is telling us
  // what they are claiming, so the form asks properly rather than posting a
  // blank.
  claimed_amount: z
    .string()
    .min(1, 'Claimed amount is required')
    .refine((value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed >= 0;
    }, 'Enter the amount being claimed as a number of zero or more'),
  spot_survey_status: z.string().optional(),
  fir_status: z.string().optional(),
  vehicle_location: z.string().optional(),
  third_party_loss: z.string().optional(),
  // DQ-346: health claims -- the hospital (picked or typed) and the stay.
  hospital_name: z.string().optional(),
  admission_date: z.string().optional(),
  discharge_date: z.string().optional(),
});

type ClaimFormData = z.infer<typeof claimSchema>;

// #633: editing a claim. The API's edit surface (ClaimUpdateSerializer) owns
// only the description, hospital and extras; the policy, claim type, incident
// and claimed amount are shown but not asked again.
const editSchema = claimSchema.partial().extend({
  incident_description: claimSchema.shape.incident_description,
});



export function ClaimFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const createMutation = useCreateClaim();
  // #633: /claims/:id/edit loads the claim; /claims/new has no :id.
  const { id: editId = '' } = useParams<{ id: string }>();
  const isEdit = !!editId;
  const updateMutation = useUpdateClaim();
  const { data: claim, isLoading: claimLoading, error: claimError } = useClaim(editId);
  // Sourced from the API, not hardcoded -- the old static list had drifted
  // from CLAIM_TYPE_CHOICES and 6 of its 8 options always failed validation.
  const { data: claimTypes = [], isLoading: claimTypesLoading } = useClaimTypes();
  const claimTypeOptions = claimTypes.map((t) => ({ value: t.value, label: t.label }));

  const isCustomer = user?.role === 'customer';
  const preselectedPolicyId = searchParams.get('policyId') || '';
  const { data: policyInfo } = usePolicy(preselectedPolicyId);
  const [photos, setPhotos] = useState<File[]>([]);
  const photoInputRef = useRef<HTMLInputElement>(null);
  // DQ-346: the network hospital chosen from the cashless locator, if any.
  const [networkHospital, setNetworkHospital] = useState<LocatorProvider | null>(null);
  const [pickingHospital, setPickingHospital] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ClaimFormData>({
    resolver: zodResolver(isEdit ? editSchema : claimSchema) as Resolver<ClaimFormData>,
    defaultValues: {
      policy: preselectedPolicyId,
    },
  });

  // The day of the week is a FACT ABOUT THE DATE, not a second question.
  // It used to be a seven-option dropdown the assessor filled in by hand, next
  // to the date that already determined it -- so the two could disagree, and
  // did not even have to: `incident_day` has no column, no serializer field and
  // no reader anywhere in the backend, so whatever was chosen was posted and
  // dropped on the floor. Derived and shown read-only instead: it cannot
  // contradict the date, and it no longer asks for something nobody stores.
  const watchedClaimType = watch('claim_type');
  const claimType = isEdit ? (claim?.claim_type ?? '') : watchedClaimType;
  const isHealthClaim = SERVER_CHECKLIST_CLAIM_TYPES.includes(claimType ?? '');
  const { data: requirements } = useQuery({
    queryKey: ['claim-document-requirements', claimType],
    queryFn: () => claimChecklistApi.requirements(claimType),
    enabled: isHealthClaim,
  });

  // #633: fill the form from the loaded claim, once.
  const filledFor = useRef('');
  useEffect(() => {
    if (!isEdit || !claim || filledFor.current === claim.id) return;
    filledFor.current = claim.id;
    const extras = (claim.claim_data ?? {}) as Record<string, unknown>;
    const text = (key: string) => (typeof extras[key] === 'string' ? (extras[key] as string) : '');
    reset({
      incident_description: claim.incident_description ?? '',
      hospital_name: claim.hospital_name ?? '',
      admission_date: (claim as { admission_date?: string | null }).admission_date ?? '',
      discharge_date: (claim as { discharge_date?: string | null }).discharge_date ?? '',
      fir_status: text('fir_status'),
      vehicle_location: text('vehicle_location'),
      third_party_loss: text('third_party_loss'),
    });
    const hospital = claim.network_hospital_detail;
    if (claim.network_hospital && hospital) {
      setNetworkHospital({
        provider_id: claim.network_hospital,
        name: hospital.name,
        phone: hospital.phone,
        address: hospital.address,
        city_name: hospital.city_name,
        pincode: hospital.pincode,
        latitude: null,
        longitude: null,
      });
    }
  }, [isEdit, claim, reset]);

  const incidentDate = watch('incident_date');
  const incidentDay = (() => {
    if (!incidentDate) return '';
    // Parsed as local midnight. `new Date('2026-08-28')` is parsed as UTC and
    // renders as the PREVIOUS day for anyone west of Greenwich.
    const [year, month, day] = incidentDate.split('-').map(Number);
    if (!year || !month || !day) return '';
    const parsed = new Date(year, month - 1, day);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleDateString(undefined, { weekday: 'long' });
  })();

  const onSubmit = (data: ClaimFormData) => {
    if (isEdit) {
      // #633: only what the edit API owns. A blank hospital date is left out
      // (the API refuses an empty date); a cleared extra is removed from
      // claim_data; extras this form does not show are kept.
      const patch: Record<string, unknown> = { incident_description: data.incident_description };
      if (isHealthClaim) {
        for (const key of ['admission_date', 'discharge_date'] as const) if (data[key]) patch[key] = data[key];
        if (networkHospital) patch.network_hospital = networkHospital.provider_id;
        else {
          patch.network_hospital = null;
          patch.hospital_name = data.hospital_name ?? '';
        }
      } else {
        patch.claim_data = mergeClaimExtras(claim?.claim_data, data);
      }
      updateMutation.mutate(
        { id: editId, data: patch as unknown as Partial<ClaimCreateRequest> },
        { onSuccess: () => navigate(`/claims/${editId}`) }
      );
      return;
    }
    const payload: Record<string, unknown> = { ...data };
    // Blank optional dates would be refused by the API; send only what was filled.
    for (const key of ['hospital_name', 'admission_date', 'discharge_date'] as const) {
      if (!isHealthClaim || !payload[key]) delete payload[key];
    }
    if (isHealthClaim && networkHospital) {
      // DQ-475: the pick wins. A name typed before choosing a hospital stays in
      // the form state; sending it would contradict the link.
      payload.network_hospital = networkHospital.provider_id;
      delete payload.hospital_name;
    }
    // #634: FIR status, vehicle location and third-party loss are motor
    // questions; a health claim must not carry values typed before the claim
    // type was switched.
    if (isHealthClaim) {
      for (const key of ['fir_status', 'vehicle_location', 'third_party_loss']) delete payload[key];
    }
    createMutation.mutate(packClaimExtras(payload) as unknown as ClaimCreateRequest, {
      onSuccess: (newClaim) => navigate(`/claims/${newClaim.id}`),
    });
  };

  if (isEdit && claimLoading) {
    return <p className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading claim...</p>;
  }
  if (isEdit && (claimError || !claim)) {
    return (
      <div>
        <div className="mb-4">
          <BackButton to="/claims" />
        </div>
        <p className="p-6 text-sm text-red-600 dark:text-red-400">This claim could not be loaded.</p>
      </div>
    );
  }

  const pageTitle = isEdit ? `Edit Claim ${claim?.claim_number ?? ''}`.trim() : 'New Claim';

  return (
    <div>
      <div className="mb-4">
        <BackButton to={isEdit ? `/claims/${editId}` : '/claims'} />
      </div>
      <PageHeader
        title={pageTitle}
        breadcrumbs={[
          { label: 'Claims', href: '/claims' },
          { label: pageTitle },
        ]}
        actions={
          <Link to={isEdit ? `/claims/${editId}` : '/claims'}>
            <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
              Cancel
            </Button>
          </Link>
        }
      />

      {preselectedPolicyId && policyInfo && (
        <Card className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
          <CardHeader title="Policy Context" subtitle="Read-only summary of the selected policy" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500 dark:text-gray-400">Insurer Policy Number</p>
              <p className="font-medium text-gray-900 dark:text-white">
                <PolicyNumber source={policyInfo} />
              </p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400">Policy Holder</p>
              <p className="font-medium text-gray-900 dark:text-white">{policyInfo.holder_name}</p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400">Insurer</p>
              <p className="font-medium text-gray-900 dark:text-white">{policyInfo.insurer_name}</p>
            </div>
            {policyInfo.vehicle_type && (
              <div>
                <p className="text-gray-500 dark:text-gray-400">Vehicle Type</p>
                <p className="font-medium text-gray-900 dark:text-white">{policyInfo.vehicle_type}</p>
              </div>
            )}
          </div>
        </Card>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          {/* #633: what the claim already says. The API does not let these be
              edited here; they are shown so the form is recognisably THIS claim. */}
          {isEdit && claim && (
            <Card>
              <CardHeader title="Claim" subtitle="Set when the claim was raised" />
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Policy</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">
                    {claim.insurer_policy_number || claim.policy_number}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Claim Type</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">
                    {claim.claim_type_display || claim.claim_type}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Incident Date</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">{claim.incident_date}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Incident Location</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">{claim.incident_location || '-'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Claimed Amount</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">{claim.claimed_amount}</dd>
                </div>
              </dl>
            </Card>
          )}

          {/* Policy Information */}
          {!isEdit && (
            <Card>
              <CardHeader title="Policy Information" subtitle="Select the policy for this claim" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {preselectedPolicyId ? (
                  // Policy is preselected — the read-only Policy Context card above
                  // already shows the policy number. Keep 'policy' registered via a
                  // hidden input so the claim is still submitted against the right policy.
                  <input type="hidden" {...register('policy')} />
                ) : (
                  <Input
                    label="Policy Number"
                    placeholder="POL- number or the insurer's policy number"
                    helperText="Enter the POL- policy number or the number printed on the insurer's document."
                    error={errors.policy?.message}
                    required
                    {...register('policy')}
                  />
                )}
                <Select
                  label="Claim Type"
                  options={claimTypeOptions}
                  disabled={claimTypesLoading}
                  error={errors.claim_type?.message}
                  required
                  {...register('claim_type')}
                />
              </div>
            </Card>
          )}

          {/* Incident Details */}
          <Card>
            <CardHeader title="Incident Details" subtitle="Describe the incident" />
            {!isEdit && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Input
                  label="Incident Date"
                  type="date"
                  error={errors.incident_date?.message}
                  required
                  {...register('incident_date')}
                />
                <Input
                  label="Incident Time"
                  type="time"
                  error={errors.incident_time?.message}
                  required
                  {...register('incident_time')}
                />
                <Input
                  // `Input` derives its id from `id || props.name`, and a
                  // read-only field registers neither -- so without this the
                  // <label> has no `htmlFor` and points at nothing, for a screen
                  // reader as much as for a test.
                  id="incident_day"
                  label="Incident Day"
                  readOnly
                  value={incidentDay}
                  placeholder="Set by the incident date"
                  className="bg-gray-50 dark:bg-gray-800"
                />
                <Input
                  label="Incident Location"
                  placeholder="Where did the incident occur?"
                  error={errors.incident_location?.message}
                  {...register('incident_location')}
                />
              </div>
            )}
            <div className={isEdit ? '' : 'mt-6'}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Incident Description <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                rows={4}
                placeholder="Describe what happened in detail..."
                {...register('incident_description')}
              />
              {errors.incident_description && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.incident_description.message}</p>
              )}
            </div>
          </Card>

          {/* DQ-346: hospital, for a cashless or reimbursement claim. */}
          {isHealthClaim && (
            <Card>
              <CardHeader title="Hospital" subtitle="Choose a network hospital, or type the name if it is not listed" />
              <div className="space-y-4">
                {networkHospital ? (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 p-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{networkHospital.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {[networkHospital.address, networkHospital.city_name, networkHospital.pincode]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={() => setNetworkHospital(null)}>
                      Change
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                    <Button type="button" variant="outline" onClick={() => setPickingHospital(true)}>
                      Choose a network hospital
                    </Button>
                    <Input
                      label="Hospital not listed? Type its name"
                      placeholder="Hospital name"
                      {...register('hospital_name')}
                    />
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="Admission Date" type="date" {...register('admission_date')} />
                  <Input label="Discharge Date" type="date" {...register('discharge_date')} />
                </div>
                {requirements && requirements.items.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Documents you will need</p>
                    <ul className="mt-1 list-disc pl-5 text-sm text-gray-600 dark:text-gray-300">
                      {requirements.items.map((item) => (
                        <li key={item.document_type}>
                          {item.label}
                          {item.requirement !== 'mandatory' &&
                            ` (${item.requirement === 'optional' ? 'optional' : item.condition_text || 'if it applies'})`}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{requirements.note}</p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Additional Details: motor only (#634); a health claim has the Hospital card above. */}
          {!isHealthClaim && (
            <Card>
              <CardHeader title="Additional Details" subtitle="FIR, vehicle location, and third-party information" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Select
                  label="FIR Status"
                  options={Object.entries(FIR_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
                  error={errors.fir_status?.message}
                  {...register('fir_status')}
                />
                <Input
                  label="Vehicle Location"
                  placeholder="Current vehicle location"
                  error={errors.vehicle_location?.message}
                  {...register('vehicle_location')}
                />
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Third Party Loss Details
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    rows={3}
                    placeholder="Describe any third-party loss or damage..."
                    {...register('third_party_loss')}
                  />
                </div>
              </div>
            </Card>
          )}

          {/* Claim Details */}
          {!isEdit && (
            <Card>
              <CardHeader title="Claim Details" subtitle="The amount claimed, and supporting details" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* B7. Shown to EVERY role, the customer included -- this is the
                    claimant's ask, not the broker's decision. The Approved and
                    Settled amounts are captured later, on the transitions that
                    decide them, and the server refuses an approval above this
                    figure. */}
                <Input
                  label="Claimed Amount"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="Amount being claimed, in rupees"
                  helperText="What you are claiming. The broker assesses and approves an amount later."
                  error={errors.claimed_amount?.message}
                  required
                  {...register('claimed_amount')}
                />
                {!isCustomer && (
                  <>
                    <Select
                      label="Spot Survey"
                      options={[
                        { value: 'pending', label: 'Pending' },
                        { value: 'done', label: 'Done' },
                        { value: 'not_required', label: 'Not Required' },
                      ]}
                      error={errors.spot_survey_status?.message}
                      {...register('spot_survey_status')}
                    />
                  </>
                )}
              </div>
            </Card>
          )}

          {/* Photo Upload */}
          {!isEdit && (
            <Card>
              <CardHeader title="Upload Photos" subtitle="Upload photos of the incident or damage" />
              <div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setPhotos((prev) => [...prev, ...files]);
                  }}
                  className="w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 dark:hover:file:bg-primary-900/30 dark:file:bg-primary-900/30 dark:file:text-primary-400"
                />
                {photos.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {photos.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => setPhotos((prev) => prev.filter((_, i) => i !== idx))}
                          className="text-red-500 text-sm hover:text-red-700 dark:hover:text-red-300"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Accepted: Images (JPG, PNG) and PDF files</p>
              </div>
            </Card>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Link to={isEdit ? `/claims/${editId}` : '/claims'}>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </Link>
            <Button type="submit" isLoading={isEdit ? updateMutation.isPending : createMutation.isPending}>
              {isEdit ? 'Save Changes' : 'Create Claim'}
            </Button>
          </div>
        </div>
      </form>

      <Modal isOpen={pickingHospital} onClose={() => setPickingHospital(false)} title="Choose a network hospital" size="full">
        <ProviderLocator
          kind="hospital"
          defaultInsurerId={insurerIdOf(policyInfo?.insurer)}
          selectLabel="Choose"
          onSelect={(provider) => {
            setNetworkHospital(provider);
            setPickingHospital(false);
          }}
        />
      </Modal>
    </div>
  );
}
